import { useActiveProject } from "@/features/project/ActiveProject";
import { type ConsentType, useConsent } from "@repo/api";
import { Status, type StatusProps } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  getConsentValue,
  translateConsentStatus,
  translateConsentType,
} from "@repo/ui";
import { getConsentStatus } from "@repo/utils";

const consentColorMap = {
  APPROVED: "positive",
  NOT_APPROVED: "indeterminate",
  PENDING: "cautionary",
  MISSING: "negative",
  ERROR: "negative",
} as const;

const getManualConsentDetails = (
  consent: boolean | undefined
): { label: string; color: StatusProps["variant"] } => {
  if (consent === true) {
    return {
      color: "positive",
      label: t("label.agree_to_contact"),
    };
  } else if (consent === false) {
    return {
      color: "indeterminate",
      label: t("label.do_not_contact"),
    };
  } else {
    return {
      color: "negative",
      label: t("prompt.consent_missing"),
    };
  }
};

export const ConsentStatus = ({
  consentType,
  manualConsent,
  displayConsentType, // display consent type in status label
}: {
  consentType?: ConsentType;
  manualConsent?: boolean;
  displayConsentType?: boolean;
}) => {
  const { opportunity } = useActiveProject();
  const { data: consentData } = useConsent(opportunity?.partyId);

  const consentValue = getConsentValue(
    consentType,
    consentData?.customerConsents,
    opportunity?.hfb,
    opportunity?.id
  );

  const consentStatus = getConsentStatus(consentValue);
  const manualConsentDetails = getManualConsentDetails(manualConsent);

  return (
    <>
      {consentType && (
        <Status
          variant={consentColorMap[consentStatus]}
          label={
            displayConsentType
              ? translateConsentType(consentType)
              : translateConsentStatus(consentStatus)
          }
        />
      )}
      {manualConsent && (
        <Status
          variant={manualConsentDetails.color}
          label={manualConsentDetails.label}
        />
      )}
    </>
  );
};
